import { useState } from 'react'
import { Building2, ShieldCheck } from 'lucide-react'

import { Button } from '@/components/ui/button'
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card'
import { useBankAuth } from '@/lib/bank/auth'

export default function BankLogin() {
  const { login, error: sessionError, isLoading } = useBankAuth()
  const [signInError, setSignInError] = useState<string | null>(null)
  const [isRedirecting, setIsRedirecting] = useState(false)

  const startSignIn = async () => {
    setSignInError(null)
    setIsRedirecting(true)
    try {
      await login()
    } catch (error) {
      setSignInError(
        error instanceof Error
          ? error.message
          : 'Bank sign-in could not be started',
      )
      setIsRedirecting(false)
    }
  }

  const error = signInError || sessionError

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center px-4 py-12">
      <div className="w-full max-w-md space-y-8">
        <div className="text-center">
          <Building2 className="mx-auto h-12 w-12 text-blue-600" />
          <h1 className="mt-6 text-3xl font-extrabold text-gray-900">
            Bank LC Workbench
          </h1>
          <p className="mt-2 text-sm text-gray-600">
            Authenticate through your bank identity provider.
          </p>
        </div>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <ShieldCheck className="h-5 w-5" />
              Secure bank sign-in
            </CardTitle>
            <CardDescription>
              Your bank controls credentials and multi-factor authentication.
              TRDRHub verifies your bank, branch, and workflow roles before
              opening the workbench.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {error && (
              <div
                role="alert"
                className="rounded-md bg-red-50 p-3 text-sm text-red-700"
              >
                {error}
              </div>
            )}
            <Button
              type="button"
              className="w-full"
              disabled={isLoading || isRedirecting}
              onClick={startSignIn}
            >
              {isRedirecting ? 'Redirecting…' : 'Continue with bank SSO'}
            </Button>
            <p className="text-center text-xs text-muted-foreground">
              For authorized bank personnel and integration principals only.
            </p>
          </CardContent>
        </Card>
      </div>
    </div>
  )
}
